<template>
  <div class="resume-optimize-payment-container">
    <div class="page-header">
      <div>
        <h2>AI 简历优化充值记录</h2>
        <p>仅展示「一元简历优化」和「5 元 10 次」套餐的支付成功记录</p>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <el-icon class="is-loading">
        <Loading />
      </el-icon>
      <span>加载中...</span>
    </div>

    <div v-else-if="resumeOptimizeRechargeRecords">
      <div class="statistics-cards">
        <el-card class="stat-card total">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><DocumentCopy /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">订单总数</div>
              <div class="stat-value">
                {{ resumeOptimizeRechargeRecords.total }}
              </div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card users">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><UserFilled /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">当前页用户</div>
              <div class="stat-value">{{ currentPageUserCount }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card amount">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Money /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">当前页实付金额</div>
              <div class="stat-value">¥{{ totalPaidAmount }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card quota">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Wallet /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">当前页优化额度</div>
              <div class="stat-value">{{ totalOptimizeQuota }} 次</div>
            </div>
          </div>
        </el-card>
      </div>

      <div class="filter-section">
        <el-input
          v-model="keyword"
          placeholder="搜索订单号/用户名/手机号"
          clearable
          style="width: 260px"
          @clear="handleSearch"
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>

        <el-select
          v-model="filterChannel"
          placeholder="筛选支付渠道"
          clearable
          style="width: 180px"
          @change="handleSearch"
        >
          <el-option label="全部渠道" value="" />
          <el-option label="支付宝" value="alipay" />
          <el-option label="微信支付" value="wechat" />
        </el-select>

        <el-select
          v-model="filterPlanId"
          placeholder="筛选套餐"
          clearable
          style="width: 200px"
          @change="handleSearch"
        >
          <el-option label="全部套餐" value="" />
          <el-option label="1 元 1 次" value="resume_optimize_once" />
          <el-option label="5 元 10 次" value="resume_optimize_pack10" />
        </el-select>

        <el-button type="primary" @click="handleSearch">
          <el-icon><Search /></el-icon>
          搜索
        </el-button>
      </div>

      <el-card class="table-card">
        <el-table
          :data="resumeOptimizeRechargeRecords.data"
          stripe
          style="width: 100%"
          empty-text="暂无 AI 简历优化充值记录"
        >
          <el-table-column label="用户" min-width="220">
            <template #default="{ row }">
              <div class="user-info">
                <el-avatar :src="row.user?.avatar" :size="40" />
                <div class="user-details">
                  <div class="username">{{ getUserName(row) }}</div>
                  <div class="user-email">{{ getUserSubText(row) }}</div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="用户权益" width="180">
            <template #default="{ row }">
              <div class="benefit-info">
                <div class="benefit-line remaining">
                  <span>剩余</span>
                  <strong>{{ formatCount(row.aiResumeOptimizeRemainingCount) }}</strong>
                  <em>次</em>
                </div>
                <div class="benefit-line used">
                  <span>已用</span>
                  <strong>{{ formatCount(row.aiResumeOptimizeUsedCount) }}</strong>
                  <em>次</em>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="订单号" min-width="240">
            <template #default="{ row }">
              <div class="order-info">
                <div class="order-id">{{ row.orderId }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="套餐/到账" width="190">
            <template #default="{ row }">
              <div class="plan-info">
                <el-tag
                  :type="getPlanType(row.planId)"
                  effect="dark"
                  size="small"
                >
                  {{ getPlanText(row.planId) }}
                </el-tag>
                <div class="plan-name">{{ row.planName || getPlanText(row.planId) }}</div>
                <div class="quota-info">到账 +{{ getOptimizeQuota(row.planId) }} 次</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="支付信息" width="220">
            <template #default="{ row }">
              <div class="payment-info">
                <div class="paid-amount">¥{{ formatAmount(getPaidAmountValue(row)) }}</div>
                <div class="payment-meta">
                  <el-tag :type="getChannelType(row.channel)" size="small">
                    {{ row.channelName }}
                  </el-tag>
                  <span>{{ formatDate(row.paidAt) }}</span>
                </div>
                <div v-if="!row.transaction" class="payment-warning">
                  未找到充值流水，已按订单金额展示
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            v-model:current-page="page"
            v-model:page-size="limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="resumeOptimizeRechargeRecords.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </el-card>
    </div>

    <div v-else class="no-data">
      <el-empty description="暂无数据" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getResumeOptimizeRechargeRecords } from '@/api/user'
import {
  DocumentCopy,
  Money,
  Wallet,
  Search,
  Loading,
  UserFilled
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const resumeOptimizeRechargeRecords = ref(null)
const loading = ref(true)
const page = ref(1)
const limit = ref(20)
const keyword = ref('')
const filterChannel = ref('')
const filterPlanId = ref('')

const currentPageRecords = computed(() => {
  return resumeOptimizeRechargeRecords.value?.data || []
})

const currentPageUserCount = computed(() => {
  const userIds = currentPageRecords.value
    .map((item) => item.user?._id || item.userId)
    .filter(Boolean)
  return new Set(userIds).size
})

const totalPaidAmount = computed(() => {
  const total = currentPageRecords.value.reduce((sum, item) => {
    return sum + Number(getPaidAmountValue(item))
  }, 0)
  return total.toFixed(2)
})

const totalOptimizeQuota = computed(() => {
  return currentPageRecords.value.reduce((sum, item) => {
    return sum + getOptimizeQuota(item.planId)
  }, 0)
})

const getResumeOptimizeRechargeRecordsData = async () => {
  try {
    loading.value = true
    const response = await getResumeOptimizeRechargeRecords(
      page.value,
      limit.value,
      keyword.value,
      filterChannel.value,
      filterPlanId.value
    )
    resumeOptimizeRechargeRecords.value = {
      total: response?.total || 0,
      page: response?.page || page.value,
      limit: response?.limit || limit.value,
      data: response?.data || []
    }
  } catch (error) {
    console.error('获取 AI 简历优化充值记录失败:', error)
    ElMessage.error('获取 AI 简历优化充值记录失败')
  } finally {
    loading.value = false
  }
}

const getUserName = (row) => {
  return (
    row.user?.wechatNickname ||
    row.user?.username ||
    row.user?.phone ||
    row.userId ||
    '未知用户'
  )
}

const getUserSubText = (row) => {
  return row.user?.email || row.user?.phone || row.user?._id || row.userId || '-'
}

const getOptimizeQuota = (planId) => {
  const quotaMap = {
    resume_optimize_once: 1,
    resume_optimize_pack10: 10
  }
  return quotaMap[planId] || 0
}

const getPlanText = (planId) => {
  const planMap = {
    resume_optimize_once: '1 元 1 次',
    resume_optimize_pack10: '5 元 10 次'
  }
  return planMap[planId] || planId || '-'
}

const formatCount = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  return Number(value || 0)
}

const getPlanType = (planId) => {
  const typeMap = {
    resume_optimize_once: 'warning',
    resume_optimize_pack10: 'success'
  }
  return typeMap[planId] || 'info'
}

const getChannelType = (channel) => {
  const typeMap = {
    alipay: 'primary',
    wechat: 'success'
  }
  return typeMap[channel] || 'info'
}

const formatAmount = (amount) => {
  return Number(amount || 0).toFixed(2)
}

const getPaidAmountValue = (row) => {
  return row.transaction?.amount ?? row.amount ?? 0
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const handleSearch = () => {
  page.value = 1
  getResumeOptimizeRechargeRecordsData()
}

const handleSizeChange = () => {
  page.value = 1
  getResumeOptimizeRechargeRecordsData()
}

const handlePageChange = () => {
  getResumeOptimizeRechargeRecordsData()
}

getResumeOptimizeRechargeRecordsData()
</script>

<style scoped lang="scss">
.resume-optimize-payment-container {
  padding: 0;
  background-color: transparent;
  min-height: 100%;

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #303133;
    }

    p {
      margin: 8px 0 0;
      color: #909399;
      font-size: 13px;
    }
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #909399;

    .el-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    span {
      font-size: 16px;
    }
  }

  .statistics-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 20px;

    .stat-card {
      transition: transform 0.3s, box-shadow 0.3s;

      .stat-content {
        display: flex;
        align-items: center;
        gap: 16px;

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        }

        .stat-info {
          flex: 1;

          .stat-label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 8px;
          }

          .stat-value {
            font-size: 28px;
            font-weight: 600;
            color: #303133;
          }
        }
      }

      &.total .stat-icon {
        color: var(--admin-color-primary);
      }

      &.users .stat-icon {
        color: var(--admin-color-primary);
      }

      &.amount .stat-icon {
        color: var(--admin-color-primary);
      }

      &.quota .stat-icon {
        color: var(--admin-color-primary);
      }
    }
  }

  .filter-section {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .table-card {
    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .user-details {
        min-width: 0;
        flex: 1;

        .username {
          font-weight: 500;
          color: #303133;
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .user-email {
          font-size: 12px;
          color: #909399;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .order-info {
      .order-id {
        font-weight: 500;
        color: #303133;
        font-size: 13px;
        word-break: break-all;
      }
    }

    .benefit-info {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .benefit-line {
        display: inline-flex;
        align-items: baseline;
        gap: 6px;
        font-size: 13px;
        color: #606266;

        span {
          color: #909399;
        }

        strong {
          font-size: 20px;
          line-height: 1;
        }

        em {
          font-style: normal;
          color: #909399;
        }

        &.remaining strong {
          color: #409eff;
        }

        &.used strong {
          color: #67c23a;
        }
      }
    }

    .plan-info {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;

      .plan-name {
        font-weight: 500;
        color: #303133;
        font-size: 13px;
      }
    }

    .quota-info {
      font-size: 13px;
      font-weight: 600;
      color: #67c23a;
    }

    .payment-info {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .paid-amount {
        font-size: 18px;
        font-weight: 700;
        color: #303133;
      }

      .payment-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #606266;

        span {
          white-space: nowrap;
        }
      }

      .payment-warning {
        font-size: 11px;
        color: #e6a23c;
      }
    }

    .pagination-container {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
  }

  .no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: white;
    border-radius: 8px;
  }
}

</style>
